import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Card, CardBody } from 'reactstrap';
import Switch from 'react-switch';
import common from 'constants/common';
import { isMobile } from 'utils/deviceHelper';
import Pagination from 'components/molecules/paginationTransition';
import withLoader from 'components/organisms/withLoader';
import { showResult } from 'utils';
import { isPatient } from 'utils/userTypeHelper';
import useUser from 'utils/useUser';
import useConfirm from 'utils/useConfirm';
import ActivityTable from './activityTable';
import ActivityTableMobile from './activityTableMobile';

const showConfirm = useConfirm({
  message: 'You have unsaved changes, are you sure you want to leave?',
  title: 'Confirm',
});
const Activity = ({
  patientGuid,
  patient,
  activitiesLoading,
  patientDetailsLoading,
}) => {
  const { userType } = useUser();
  const { unsavedComments } = useSelector((state) => state.doctor);
  const activity = useSelector((state) => state.activity);
  const device = useSelector((state) => state.user.deviceType);
  const {
    submitComment: submitCommentLoading,
    actionReview: actionReviewLoading,
  } = useSelector((state) => state.loading.effects.activity);

  const {
    activity: {
      setActivitiesPage,
      submitComment,
      actionReview,
      changeReviewed,
    },
    doctor: { clearComments },
  } = useDispatch();

  const { reviewed } = activity;
  const { clinicGuid } = patient?.clinic;

  const toggleReviewed = () => {
    changeReviewed({ patientGuid, clinicGuid });
  };

  const setPage = (pageOffset, page) => {
    setActivitiesPage({
      patientGuid,
      clinicGuid,
      pageOffset,
      userType,
      goToPage: page,
    });
  };

  const handleCommentSubmit = async (id, deviceId, values, resetForm) => {
    const postData = {
      readingId: id,
      deviceId,
      message: values.comment,
    };
    const { error } = await submitComment(postData);
    if (error) {
      showResult(error);
    }
    if (error === undefined) {
      resetForm({});
    }
  };

  const ignoreUnsavedChanges =
    (ok, cancel) =>
    async (...props) => {
      let confirm = true;
      if (Object.keys(unsavedComments).length) {
        confirm = await showConfirm();
      }
      if (confirm) {
        clearComments();
        return ok && ok(...props);
      }
      return cancel && cancel(...props);
    };

  const handleReviewedCheck = async (id, deviceId) => {
    const postData = {
      readingId: id,
      deviceId,
      reviewed: true,
    };
    const { error } = await actionReview(postData);
    showResult(error, common.submitSuccessTypes.reviewed);
  };

  return (
    <div className="activity">
      <Card className="card-border">
        <CardBody>
          <div className="activity-header">
            <h3>Activity</h3>
            <div className="activity-switcher">
              {!isPatient(userType) && (
                <>
                  <p>{reviewed ? 'Actioned' : 'Unactioned'}</p>
                  <Switch
                    className="evelyn-switch"
                    onChange={ignoreUnsavedChanges(toggleReviewed)}
                    checked={reviewed}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    onHandleColor={common.commonColors.green}
                    offHandleColor={common.commonColors.gray}
                    onColor={common.commonColors.white}
                    offColor={common.commonColors.white}
                    handleDiameter={24}
                    height={30}
                    width={59}
                    activeBoxShadow="0"
                  />
                </>
              )}
            </div>
          </div>
          {withLoader(
            activitiesLoading || patientDetailsLoading,
            <div className="activity-body">
              {isMobile(device) && (
                <ActivityTableMobile
                  handleCommentSubmit={handleCommentSubmit}
                  handleReviewedCheck={handleReviewedCheck}
                  submitCommentLoading={
                    submitCommentLoading || actionReviewLoading
                  }
                  actionReviewLoading={
                    submitCommentLoading || actionReviewLoading
                  }
                  patient={patient}
                  reviewed={reviewed}
                  activity={activity}
                />
              )}
              {!isMobile(device) && (
                <ActivityTable
                  handleCommentSubmit={handleCommentSubmit}
                  handleReviewedCheck={handleReviewedCheck}
                  submitCommentLoading={
                    submitCommentLoading || actionReviewLoading
                  }
                  actionReviewLoading={
                    submitCommentLoading || actionReviewLoading
                  }
                  patient={patient}
                  reviewed={reviewed}
                  activity={activity}
                />
              )}
              <Pagination
                title=""
                page={activity.page}
                pageTotal={activity.data.length}
                itemsPerPage={activity.itemsPerPage}
                total={activity.totalCount}
                handlePrevClick={() => setPage(-1)}
                handleNextClick={() => setPage(+1)}
                handleFirstClick={() => setPage(-(activity.page - 1))}
                handleLastClick={() =>
                  setPage(
                    Math.ceil(activity.totalCount / activity.itemsPerPage),
                  )
                }
                loading={activitiesLoading}
                handleGoToPage={(num) => setPage(null, num)}
              />
            </div>,
          )}
        </CardBody>
      </Card>
    </div>
  );
};

Activity.propTypes = {
  patientGuid: PropTypes.string.isRequired,
  patientDetailsLoading: PropTypes.bool.isRequired,
  activitiesLoading: PropTypes.bool.isRequired,
  patient: PropTypes.shape(),
};

export default Activity;
